import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import { COLOR_WHITE, COLOR_ADDITIONAL_WARM_GRAY_3U } from "~/theme/colors";
import { getFormatMedia } from "~/theme/utils";

import { EDITORIAL_CONTENT_CLASSES, MASONRY_GUTTER } from "./constants";

export const EditorialContentContainer = styled(Box)(({ theme }) => {
  const { BREAKPOINT_TABLET, BREAKPOINT_DESKTOP, BREAKPOINT_WIDE_DESKTOP } =
    getFormatMedia(theme);
  const BELOW_TABLET = theme.breakpoints.down("sm");
  const TABLET_ONLY = theme.breakpoints.only("sm");

  return {
    paddingBottom: theme.spacing(7),
    backgroundImage: `linear-gradient(to bottom, 
          ${COLOR_WHITE} 0 31%,
          ${COLOR_ADDITIONAL_WARM_GRAY_3U} 31% 100%
        )`,
    [BREAKPOINT_TABLET]: {
      paddingBottom: theme.spacing(3),
      backgroundImage: `linear-gradient(to bottom, 
          ${COLOR_WHITE} 0 21%,
          ${COLOR_ADDITIONAL_WARM_GRAY_3U} 21% 100%
        )`,
    },
    [BREAKPOINT_DESKTOP]: {
      paddingBottom: theme.spacing(10),
      backgroundImage: `linear-gradient(to bottom, 
          ${COLOR_WHITE} 0 25.5%,
          ${COLOR_ADDITIONAL_WARM_GRAY_3U} 25.5% 100%
        )`,
    },
    [BREAKPOINT_WIDE_DESKTOP]: {
      backgroundImage: `linear-gradient(to bottom, 
          ${COLOR_WHITE} 0 26%,
          ${COLOR_ADDITIONAL_WARM_GRAY_3U} 26% 100%
        )`,
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.header}`]: {
      position: "relative",
      zIndex: 3,
      [BREAKPOINT_TABLET]: {
        marginBottom: theme.spacing(6),
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.mobileHeaderBox}`]: {
      marginBottom: -20,
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.grid}`]: {
      margin: "0 auto",
      [BREAKPOINT_TABLET]: {
        paddingInline: "12%",
      },
      [BREAKPOINT_DESKTOP]: {
        paddingInline: "20%",
      },
      [BREAKPOINT_WIDE_DESKTOP]: {
        paddingInline: 0,
        maxWidth: 1180,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonry}`]: {
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
      width: "100%",
      gap: MASONRY_GUTTER,
      [BREAKPOINT_TABLET]: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gridAutoRows: "auto 1fr",
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonryColumn}`]: {
      display: "contents",
      [BREAKPOINT_TABLET]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        gap: MASONRY_GUTTER,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonryColumnLeft}`]: {
      [BREAKPOINT_TABLET]: {
        gridColumn: 1,
        gridRow: 2,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonryColumnRight}`]: {
      [BREAKPOINT_TABLET]: {
        gridColumn: 2,
        gridRow: "1 / span 2",
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonryHeaderCell}`]: {
      display: "flex",
      justifyContent: "stretch",
      order: -1,
      [BREAKPOINT_TABLET]: {
        gridColumn: 1,
        gridRow: 1,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.masonry}.${EDITORIAL_CONTENT_CLASSES.masonrySourceOrder}`]:
      {
        [BREAKPOINT_TABLET]: {
          gridAutoRows: "auto",
          gridAutoFlow: "row dense",
          alignItems: "start",
          [`& > .${EDITORIAL_CONTENT_CLASSES.gridItemRight}`]: {
            gridColumn: 2,
          },
          [`& > .${EDITORIAL_CONTENT_CLASSES.gridItemLeft}`]: {
            gridColumn: 1,
          },
        },
        [`& > .${EDITORIAL_CONTENT_CLASSES.masonryHeaderCell}`]: {
          [TABLET_ONLY]: {
            display: "none",
          },
        },
      },
    [`& .${EDITORIAL_CONTENT_CLASSES.gridItem}`]: {
      width: "100%",
      marginBottom: theme.spacing(5),
      [BREAKPOINT_TABLET]: {
        marginBottom: 0,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.gridItemLeft}`]: {
      paddingRight: "17%",
      [BREAKPOINT_TABLET]: {
        paddingRight: theme.spacing(1.5),
      },
      [BREAKPOINT_DESKTOP]: {
        paddingRight: theme.spacing(3),
      },
      [BREAKPOINT_WIDE_DESKTOP]: {
        paddingRight: theme.spacing(4),
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.gridItemRight}`]: {
      paddingLeft: "17%",
      [BREAKPOINT_TABLET]: {
        paddingLeft: theme.spacing(1.5),
      },
      [BREAKPOINT_DESKTOP]: {
        paddingLeft: theme.spacing(3),
      },
      [BREAKPOINT_WIDE_DESKTOP]: {
        paddingLeft: theme.spacing(4),
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.gridItemLast}`]: {
      marginBottom: 0,
    },
    [BELOW_TABLET]: {
      [`& .${EDITORIAL_CONTENT_CLASSES.gridItemLastOnMobile}`]: {
        marginBottom: 0,
      },
      [`& .${EDITORIAL_CONTENT_CLASSES.gridItemHiddenOnMobile}`]: {
        display: "none",
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.title}`]: {
      ...theme.typography.h3,
      [BREAKPOINT_TABLET]: {
        ...theme.typography.h2,
        marginBottom: theme.spacing(2),
      },
      [BREAKPOINT_DESKTOP]: {
        ...theme.typography.h1,
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.subtitle}`]: {
      marginBottom: theme.spacing(1),
      [BREAKPOINT_TABLET]: {
        marginBottom: theme.spacing(2),
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.linkDesktop}`]: {
      display: "none",
      [BREAKPOINT_TABLET]: {
        display: "block",
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.linkMobile}`]: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(3),
      [BREAKPOINT_TABLET]: {
        display: "none",
      },
    },
    [`& .${EDITORIAL_CONTENT_CLASSES.ctaLinkButton}`]: {
      "& > p": {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: 18,
      },
    },
  };
});
